// contexts/StepperContext.tsx
import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface StepperContextType {
  selectedCaseRecordId: number | null;
  selectedPrescriptionId: number | null;
  setSelectedCaseRecordId: (id: number | null) => void;
  setSelectedPrescriptionId: (id: number | null) => void;
  clearSelections: () => void;
  isStepperActive: boolean;
  setStepperActive: (active: boolean) => void;
  // Add a flag to track if mapping should happen
  shouldMapCaseRecord: boolean;
  shouldMapPrescription: boolean;
  setShouldMapCaseRecord: (should: boolean) => void;
  setShouldMapPrescription: (should: boolean) => void;
}

const StepperContext = createContext<StepperContextType | undefined>(undefined);

export const StepperProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedCaseRecordId, setSelectedCaseRecordId] = useState<number | null>(null);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<number | null>(null);
  const [isStepperActive, setStepperActive] = useState(false);
  const [shouldMapCaseRecord, setShouldMapCaseRecord] = useState(false);
  const [shouldMapPrescription, setShouldMapPrescription] = useState(false);
  const location = useLocation();

  const clearSelections = () => {
    setSelectedCaseRecordId(null);
    setSelectedPrescriptionId(null);
    setShouldMapCaseRecord(false);
    setShouldMapPrescription(false);
    setStepperActive(false);
  };

  // Clear selections when navigating away from create-order routes
  useEffect(() => {
    const isCreateOrderRoute = location.pathname.includes('/create-order') || 
                              location.pathname.includes('/vsp/create-order') ||
                              location.pathname.includes('/customer/create-order');
    
    if (!isCreateOrderRoute && isStepperActive) {
      clearSelections();
    }
  }, [location.pathname]);

  return (
    <StepperContext.Provider
      value={{
        selectedCaseRecordId,
        selectedPrescriptionId,
        setSelectedCaseRecordId,
        setSelectedPrescriptionId,
        clearSelections,
        isStepperActive,
        setStepperActive,
        shouldMapCaseRecord,
        shouldMapPrescription,
        setShouldMapCaseRecord,
        setShouldMapPrescription,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};

export const useStepper = () => {
  const context = useContext(StepperContext);
  if (!context) {
    throw new Error('useStepper must be used within a StepperProvider');
  }
  return context;
};